// RunReport v1 — FE mirror + the pure run collector (D-PAP-40/41).
//
// ⚠️ Keep the types, version, and caps in LOCKSTEP with
// platform-backend/src/code-sessions/run-report.ts (the zod source of truth).
//
// After an applied game turn the studio runs the game instrumented; this
// collector folds everything the sandbox reports (console lines, engine frame
// stats, asset outcomes, the run-probe canvas sample) into the structured
// RunReport the backend adjudicates. Pure — NO network in this file; posting
// lives in codeApi (`postRunReport`) and the loop in `panes/useVerification`.

import {
  ASSET_MANIFEST_PREFIX_CHARS,
  type AssetManifestEntry,
  type ConsoleLine,
  type GameEngine,
} from './buildGamePreview';
import { isFailureWarn } from './verifyRoundtrip';

export const RUN_REPORT_VERSION = 1;

/** How long a run is observed before the probe is asked for its report (ms).
 *  Shared by GameFrame and SiteFrame — one observation policy for both. */
export const RUN_OBSERVE_MS = 4000;
/** How long to wait for the probe's reply before finalizing without it (ms). */
export const PROBE_REPLY_TIMEOUT_MS = 1500;

// Caps (mirror the backend schema). Overflow is TRANSPARENT — `dropped` counts
// what didn't fit, never silent truncation.
export const MAX_REPORT_ERRORS = 6;
export const MAX_REPORT_WARNS = 4;
export const MAX_REPORT_REJECTIONS = 3;
export const MAX_REPORT_WINDOW_ERRORS = 3;
export const MAX_REPORT_LINE_CHARS = 300;
export const MAX_RUNTIME_ERRORS = 3;
export const MAX_RUNTIME_ERROR_TEXT = 1_000;
export const MAX_RUNTIME_ERROR_STACK = 4_000;
export const MAX_REPORT_ASSETS = 20;
// Screenshot evidence caps (D-HARN-21b; mirror the backend request schema).
export const MAX_SCREENSHOT_DATAURL_CHARS = 200_000;
export const SCREENSHOT_MIN_DIM = 16;
export const SCREENSHOT_MAX_DIM = 1024;
/** The only wire-legal screenshot encodings (frozen cross-repo contract). */
export const SCREENSHOT_DATAURL_RE = /^data:image\/(png|jpeg|webp);base64,/;
const MAX_REPORT_PATH_CHARS = 200;
const MAX_REPORT_DETAIL_CHARS = 200;
const MAX_OBSERVED_MS = 120_000;
const MAX_FRAMES = 1_000_000;
const MAX_FPS = 1000;
const MAX_ATTEMPT = 10;
const MAX_CANVAS_SAMPLED = 4096;

/** How the console shim prefixes an unhandled promise rejection (buildPreview). */
const REJECTION_PREFIX = 'Unhandled promise:';

// ── Website run evidence (creative-code-studio-website-prd D-WEB-13) ─────────
// The site-specific half of a website RunReport — mirror the backend's
// `SiteReportSchema`. A website's observable truth is: did the page load, which
// /api routes were ACTUALLY called (and with what status), which buttons are
// actually WIRED, and what the kid/AI's own console.log instrumentation said.
export const MAX_SITE_API_CALLS = 30;
export const MAX_SITE_BUTTONS = 30;
export const MAX_SITE_LOGS = 10;
const MAX_SITE_PATH_CHARS = 120;
const MAX_SITE_PAGE_CHARS = 120;
const MAX_SITE_SELECTOR_CHARS = 80;
const MAX_SITE_STATUS = 599;

export type SiteApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OTHER';
const SITE_API_METHODS: readonly string[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

export interface SiteApiCall {
  method: SiteApiMethod;
  path: string;
  /** 0 = the fetch rejected before any response (blocked URL). */
  status: number;
}

export interface SiteReport {
  /** DOM parsed + kid scripts executed (the site runtime's loaded signal). */
  pageLoaded: boolean;
  /** Which page was showing during the observed window. */
  page: string;
  apiCalls: SiteApiCall[];
  /** Interactive elements on the page + whether a click/submit listener is
   *  attached to them (the "Add task does nothing" evidence). */
  buttons: Array<{ selector: string; wired: boolean }>;
  /** True when a click listener exists on document/body — delegation makes the
   *  per-button ledger inconclusive (the backend then skips the unwired check). */
  delegatedClickHandler: boolean;
  /** console.log/info lines — the model's own instrumentation echoed back. */
  logs: string[];
}

/** The engines a RunReport can carry — the game engines plus `website`. */
export type RunReportEngine = GameEngine | 'website';

/** Per-asset load outcome. `missing-ref` = a raw path the inliner never rewrote
 *  (not in the VFS — the silent broken-GLB signature); `failed` = present but
 *  the loader errored. */
export interface AssetOutcome {
  path: string;
  status: 'loaded' | 'failed' | 'missing-ref' | 'unknown';
  detail?: string;
}

/**
 * Screenshot evidence riding the RunReport REQUEST (D-HARN-21b). Attached by
 * `useVerification` ONLY when the backend requested it (`screenshot_requested`)
 * — never collected here, never persisted server-side (the evidence report the
 * backend stores stays lean). `dataUrl` must match {@link SCREENSHOT_DATAURL_RE}
 * and stay ≤ {@link MAX_SCREENSHOT_DATAURL_CHARS}; dims are the screenshot's own
 * pixel size, {@link SCREENSHOT_MIN_DIM}–{@link SCREENSHOT_MAX_DIM}.
 */
export interface RunReportScreenshot {
  dataUrl: string;
  width: number;
  height: number;
}

export interface RuntimeErrorDetail {
  text: string;
  stack?: string;
  location?: string;
  muted?: boolean;
}

export interface RunReport {
  /** Bounded original diagnostics for automatic fixes; absent on older clients. */
  runtimeErrors?: RuntimeErrorDetail[];
  reportVersion: typeof RUN_REPORT_VERSION;
  /** 1-based verify attempt for this chain (run after the turn = 1; after fix #1 = 2…). */
  attempt: number;
  engine: RunReportEngine;
  /** Present EXACTLY when engine === 'website'. Website reports keep the
   *  game-shaped fields degenerate: `booted` mirrors `site.pageLoaded`,
   *  framesAdvanced/fps are 0, canvas is absent-shaped; `assets` carries the
   *  asset-origin shim's run-time outcomes. */
  site?: SiteReport;
  /** How long the game was observed before the report was cut (ms). */
  observedMs: number;
  /** Engine frames advanced at least once. */
  booted: boolean;
  /** Cumulative engine frames seen in the window — 0 means frozen/dead. */
  framesAdvanced: number;
  fps: number;
  consoleErrors: string[];
  consoleWarns: string[];
  unhandledRejections: string[];
  windowErrors: string[];
  /** Entries the caps dropped — transparency, never silent truncation. */
  dropped: { errors: number; warns: number; rejections: number };
  assets: AssetOutcome[];
  canvas: { present: boolean; nonBlank: boolean | null; sampled: number };
  /** Set when the PROBE itself failed — the backend treats the run as
   *  inconclusive, never as a failure of the kid's game. */
  probeError?: string;
  /** Screenshot evidence — ONLY when the backend requested it AND capture
   *  succeeded; ANY capture failure omits the field (the report still posts). */
  screenshot?: RunReportScreenshot;
}

// ── Frame → parent message guards ───────────────────────────────────────────

/** The run probe's canvas sample (see RUN_PROBE in buildGamePreview). */
export interface RunProbeCanvas {
  present: boolean;
  nonBlank: boolean | null;
  sampled: number;
}

export function isRunReportMessage(
  data: unknown,
): data is { __airbotixRunReport: true; canvas: RunProbeCanvas } {
  return (
    typeof data === 'object' &&
    data !== null &&
    '__airbotixRunReport' in data &&
    (data as { __airbotixRunReport: unknown }).__airbotixRunReport === true
  );
}

/** One asset outcome from the frame. Two senders, one message:
 *  - the three.js loader guard (buildGamePreview THREE_LOADER_GUARD) posts
 *    `url` truncated to the manifest prefix + `len` (the FULL url length);
 *  - the asset-origin shim (`../code/assetOrigin.ts`) posts the kid's `path`
 *    directly, with `missing: true` for a project-shaped reference that matched
 *    no project file (the run-time `missing-ref` signature). */
export interface AssetLoadMessage {
  url?: string;
  len?: number;
  /** The project path as the asset-origin shim resolved (or failed to resolve) it. */
  path?: string;
  /** True when `path` names no project file (asset-origin shim only). */
  missing?: boolean;
  ok: boolean;
  error?: string;
}

export function isAssetMessage(data: unknown): data is { __airbotixAsset: true } & AssetLoadMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    '__airbotixAsset' in data &&
    (data as { __airbotixAsset: unknown }).__airbotixAsset === true
  );
}

/** The site runtime shim's reply to the `report` control request (D-WEB-13).
 *  `site` stays `unknown` — the frame is untrusted; `feedSite` sanitizes. */
export function isSiteReportMessage(
  data: unknown,
): data is { __airbotixSiteReport: true; site: unknown } {
  return (
    typeof data === 'object' &&
    data !== null &&
    '__airbotixSiteReport' in data &&
    (data as { __airbotixSiteReport: unknown }).__airbotixSiteReport === true
  );
}

// ── The collector ────────────────────────────────────────────────────────────

export interface RunCollectorOptions {
  engine: RunReportEngine;
  /** 1-based chain attempt this run reports as. */
  attempt: number;
  /** Inlined-asset identities from buildGamePreview — maps a reported data: URL
   *  back to the kid's path (prefix + length match). Empty for websites. */
  assetManifest: AssetManifestEntry[];
}

export interface RunCollector {
  /** Feed a captured console line (AFTER resolveErrorLoc, so locs are kid files). */
  feedConsole(line: ConsoleLine): void;
  /** Feed an engine stat tick (cumulative frames + current fps). */
  feedFrames(frames: number, fps: number): void;
  /** Feed a loader-guard asset outcome. */
  feedAsset(msg: AssetLoadMessage): void;
  /** Feed the run probe's canvas sample. */
  feedProbe(canvas: RunProbeCanvas): void;
  /** Feed the site runtime's report reply (engine 'website' only, D-WEB-13).
   *  The payload comes from the untrusted frame — sanitized/clamped here. */
  feedSite(site: unknown): void;
  /** Record that the probe itself failed (first reason wins). */
  setProbeError(reason: string): void;
  /** Cut the report. The caller owns emit-once semantics. */
  finalize(observedMs: number): RunReport;
}

const clampInt = (n: number, min: number, max: number): number =>
  Math.min(Math.max(Math.floor(Number.isFinite(n) ? n : 0), min), max);

/** Sanitize the untrusted site-report reply into the wire-legal SiteReport
 *  (logs are collected parent-side and merged at finalize). Null when the
 *  payload isn't even object-shaped — the report then ships without `site`
 *  (the backend treats that as not-loaded/inconclusive, never as broken). */
function sanitizeSiteReply(raw: unknown): Omit<SiteReport, 'logs'> | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const apiCalls: SiteApiCall[] = [];
  if (Array.isArray(r.apiCalls)) {
    for (const entry of r.apiCalls.slice(0, MAX_SITE_API_CALLS)) {
      if (typeof entry !== 'object' || entry === null) continue;
      const c = entry as Record<string, unknown>;
      const method = String(c.method ?? '');
      apiCalls.push({
        method: SITE_API_METHODS.includes(method) ? (method as SiteApiMethod) : 'OTHER',
        path: String(c.path ?? '').slice(0, MAX_SITE_PATH_CHARS),
        status: clampInt(Number(c.status), 0, MAX_SITE_STATUS),
      });
    }
  }
  const buttons: SiteReport['buttons'] = [];
  if (Array.isArray(r.buttons)) {
    for (const entry of r.buttons.slice(0, MAX_SITE_BUTTONS)) {
      if (typeof entry !== 'object' || entry === null) continue;
      const b = entry as Record<string, unknown>;
      buttons.push({
        selector: String(b.selector ?? '').slice(0, MAX_SITE_SELECTOR_CHARS),
        wired: b.wired === true,
      });
    }
  }
  return {
    pageLoaded: r.pageLoaded === true,
    page: String(r.page ?? '').slice(0, MAX_SITE_PAGE_CHARS),
    apiCalls,
    buttons,
    delegatedClickHandler: r.delegatedClickHandler === true,
  };
}

/**
 * Build a collector for one game run. Pure state-folding: classification,
 * caps + `dropped` accounting, de-duping, and manifest mapping all live here so
 * they're unit-testable without an iframe.
 */
export function createRunCollector(opts: RunCollectorOptions): RunCollector {
  const consoleErrors: string[] = [];
  const consoleWarns: string[] = [];
  const unhandledRejections: string[] = [];
  const windowErrors: string[] = [];
  const runtimeErrors: RuntimeErrorDetail[] = [];
  const runtimeErrorKeys = new Set<string>();
  const dropped = { errors: 0, warns: 0, rejections: 0 };
  const seen = new Set<string>();
  const assets: AssetOutcome[] = [];
  const assetPaths = new Set<string>();
  let framesAdvanced = 0;
  let fps = 0;
  let canvas: RunProbeCanvas = { present: false, nonBlank: null, sampled: 0 };
  let probeError: string | undefined;
  // Website evidence (D-WEB-13): the shim's sanitized reply + the log/info
  // console lines collected parent-side (the model's own instrumentation).
  let site: Omit<SiteReport, 'logs'> | null = null;
  const siteLogs: string[] = [];

  /** Clip → de-dupe (repeats merge, they're not "dropped") → cap (overflow counts). */
  const addLine = (bucket: string[], cap: number, key: string, text: string, onDrop: () => void) => {
    const clipped = text.slice(0, MAX_REPORT_LINE_CHARS);
    const dedupeKey = `${key}:${clipped}`;
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    if (bucket.length >= cap) {
      onDrop();
      return;
    }
    bucket.push(clipped);
  };

  return {
    feedConsole(line) {
      const text = (line.text ?? '').trim();
      if (!text || text === 'ready') return; // the shim's handshake, never a bug
      // Website reports echo the kid/AI's own console.log/info instrumentation
      // back (site.logs, D-WEB-13) — the model's test probe. Duplicates are
      // KEPT ("clicked" three times is signal); the cap simply cuts.
      if (opts.engine === 'website' && (line.level === 'log' || line.level === 'info')) {
        if (siteLogs.length < MAX_SITE_LOGS) siteLogs.push(text.slice(0, MAX_REPORT_LINE_CHARS));
        return;
      }
      if (line.level === 'error') {
        const clip = (value: string, cap: number) => value.length > cap
          ? value.slice(0, cap - 12) + '\n[truncated]' : value;
        const detail: RuntimeErrorDetail = {
          text: clip(line.text, MAX_RUNTIME_ERROR_TEXT),
          ...(typeof line.stack === 'string' ? { stack: clip(line.stack, MAX_RUNTIME_ERROR_STACK) } : {}),
          ...(line.loc ? { location: `${line.loc.file}:${line.loc.line}:${line.loc.col}`.slice(0, 300) } : {}),
          ...(line.muted ? { muted: true } : {}),
        };
        const key = JSON.stringify(detail);
        if (!runtimeErrorKeys.has(key) && runtimeErrors.length < MAX_RUNTIME_ERRORS) {
          runtimeErrorKeys.add(key);
          runtimeErrors.push(detail);
        }
        if (text.startsWith(REJECTION_PREFIX)) {
          addLine(unhandledRejections, MAX_REPORT_REJECTIONS, 'rej', text, () => {
            dropped.rejections += 1;
          });
          return;
        }
        if (line.loc) {
          // An uncaught window.onerror line — loc-resolved to the kid's file.
          // Its overflow counts under `dropped.errors` (error-class; the wire
          // shape has no window-error counter).
          addLine(
            windowErrors,
            MAX_REPORT_WINDOW_ERRORS,
            'win',
            `${text} (${line.loc.file}:${line.loc.line})`,
            () => {
              dropped.errors += 1;
            },
          );
          return;
        }
        addLine(consoleErrors, MAX_REPORT_ERRORS, 'err', text, () => {
          dropped.errors += 1;
        });
        return;
      }
      if (line.level === 'warn') {
        // Curated failure warns are PROMOTED to consoleErrors: the backend's
        // clean-run predicate treats warns as advisory, so a Phaser missing
        // texture (reported via console.warn) must land error-class or a broken
        // game would verify clean. Generic kid warns stay advisory.
        if (isFailureWarn(text)) {
          addLine(consoleErrors, MAX_REPORT_ERRORS, 'err', text, () => {
            dropped.errors += 1;
          });
          return;
        }
        addLine(consoleWarns, MAX_REPORT_WARNS, 'warn', text, () => {
          dropped.warns += 1;
        });
      }
    },

    feedFrames(frames, statFps) {
      framesAdvanced = Math.max(framesAdvanced, clampInt(frames, 0, MAX_FRAMES));
      fps = Math.min(Math.max(Number.isFinite(statFps) ? statFps : 0, 0), MAX_FPS);
    },

    feedAsset(msg) {
      const url = String(msg.url ?? '');
      let path: string;
      let status: AssetOutcome['status'];
      if (typeof msg.path === 'string' && msg.path.length > 0) {
        // The asset-origin shim already knows the kid's path (run-time
        // resolution): a project-shaped reference that matched nothing is the
        // `missing-ref` signature; otherwise the element/fetch outcome decides.
        path = msg.path.slice(0, MAX_REPORT_PATH_CHARS);
        status = msg.missing === true ? 'missing-ref' : msg.ok ? 'loaded' : 'failed';
      } else if (url.startsWith('data:')) {
        // Inlined VFS asset — map back to the kid's path by prefix+length. An
        // unmatched data: URL (constructed at runtime) keeps its truncated url
        // so the evidence isn't lost.
        const prefix = url.slice(0, ASSET_MANIFEST_PREFIX_CHARS);
        const hit = opts.assetManifest.find((m) => m.prefix === prefix && m.length === msg.len);
        path = (hit ? hit.path : url).slice(0, MAX_REPORT_PATH_CHARS);
        status = msg.ok ? 'loaded' : 'failed';
      } else if (/^https?:/i.test(url)) {
        path = url.slice(0, MAX_REPORT_PATH_CHARS);
        status = msg.ok ? 'loaded' : 'failed';
      } else {
        // A raw relative path the inliner never rewrote — it is NOT in the VFS,
        // the silent-missing-asset signature, regardless of what the fetch did.
        path = url.slice(0, MAX_REPORT_PATH_CHARS);
        status = 'missing-ref';
      }
      if (assetPaths.has(path) || assets.length >= MAX_REPORT_ASSETS) return;
      assetPaths.add(path);
      const detail = msg.error ? String(msg.error).slice(0, MAX_REPORT_DETAIL_CHARS) : undefined;
      assets.push({ path, status, ...(detail ? { detail } : {}) });
    },

    feedProbe(sample) {
      canvas = {
        present: sample.present === true,
        nonBlank: typeof sample.nonBlank === 'boolean' ? sample.nonBlank : null,
        sampled: clampInt(sample.sampled, 0, MAX_CANVAS_SAMPLED),
      };
    },

    feedSite(raw) {
      if (opts.engine !== 'website') return;
      site = sanitizeSiteReply(raw) ?? site;
    },

    setProbeError(reason) {
      if (probeError === undefined) probeError = reason.slice(0, MAX_REPORT_LINE_CHARS);
    },

    finalize(observedMs) {
      if (opts.engine === 'website') {
        // Game-shaped fields go degenerate (backend contract): booted mirrors
        // site.pageLoaded, frames/fps 0, canvas absent-shaped. `assets` carries
        // the asset-origin shim's run-time outcomes (a db row naming a picture
        // that is not in the project = `missing-ref`). No shim reply ⇒ no `site`
        // at all — the backend degrades that to not-loaded/inconclusive, never
        // to "broken".
        return {
          reportVersion: RUN_REPORT_VERSION,
          attempt: clampInt(opts.attempt, 1, MAX_ATTEMPT),
          engine: 'website',
          ...(site !== null ? { site: { ...site, logs: [...siteLogs] } } : {}),
          observedMs: clampInt(observedMs, 0, MAX_OBSERVED_MS),
          booted: site?.pageLoaded === true,
          framesAdvanced: 0,
          fps: 0,
          consoleErrors: [...consoleErrors],
          consoleWarns: [...consoleWarns],
          unhandledRejections: [...unhandledRejections],
          windowErrors: [...windowErrors],
          ...(runtimeErrors.length ? { runtimeErrors: runtimeErrors.map((error) => ({ ...error })) } : {}),
          dropped: { ...dropped },
          assets: [...assets],
          canvas: { present: false, nonBlank: null, sampled: 0 },
          ...(probeError !== undefined ? { probeError } : {}),
        };
      }
      return {
        reportVersion: RUN_REPORT_VERSION,
        attempt: clampInt(opts.attempt, 1, MAX_ATTEMPT),
        engine: opts.engine,
        observedMs: clampInt(observedMs, 0, MAX_OBSERVED_MS),
        booted: framesAdvanced > 0,
        framesAdvanced,
        fps,
        consoleErrors: [...consoleErrors],
        consoleWarns: [...consoleWarns],
        unhandledRejections: [...unhandledRejections],
        windowErrors: [...windowErrors],
        ...(runtimeErrors.length ? { runtimeErrors: runtimeErrors.map((error) => ({ ...error })) } : {}),
        dropped: { ...dropped },
        assets: [...assets],
        canvas: { ...canvas },
        ...(probeError !== undefined ? { probeError } : {}),
      };
    },
  };
}
